import createError from 'create-error';
import config from '../config/config';

const GA4_KEY_EVENTS = new Set([
    'user_signed_up',
    'share_link_created',
]);

const GA4_EVENT_ALIASES = {
    image_result_downloaded: 'result_downloaded',
};

export function getMixpanelDeviceId() {
    try {
        return window.mixpanel?.get_property?.('$device_id') || null;
    } catch {
        return null;
    }
}

/** Mirror product analytics to GA4 (Admin must register page, entry, source, page_variant as custom dimensions). */
export function trackGa4Event(eventName, params = {}) {
    if (typeof window.gtag !== 'function') return;

    const name = GA4_EVENT_ALIASES[eventName] || eventName;
    const gaParams = { ...params };
    if (GA4_KEY_EVENTS.has(eventName)) {
        gaParams.value = 1;
    }
    window.gtag('event', name, gaParams);
}

export const InvalidFileTypeError = createError('Invalid file type');

export async function validateExt(file) {
    const ext = file.name.toLowerCase().split('.').pop();
    if (!config.allowedExtensions.includes(ext)) {
        return Promise.reject(new InvalidFileTypeError('Invalid file type', {tooltip: `The supported input types are: ${config.allowedExtensions}`}));
    }

    return Promise.resolve();
}

export function getMediaType(filename) {
    const ext = filename?.toLowerCase().split('.').pop();
    if (config.videoExtensions.includes(ext)) { return 'video'; }
    if (config.imageExtensions.includes(ext)) { return 'image'; }
    return 'unknown';
}

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

function getThumbnailSize(width, height, maxWidth = 500, maxHeight = 500) {
    const scale = Math.min(Math.min(maxWidth / width, maxHeight / height), 1);
    return {
        thumbWidth: Math.max(1, Math.round(width * scale)),
        thumbHeight: Math.max(1, Math.round(height * scale)),
    };
}

function createThumbnailUrl(source, width, height) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const {thumbWidth, thumbHeight} = getThumbnailSize(width, height);

    canvas.width = thumbWidth;
    canvas.height = thumbHeight;
    ctx.drawImage(source, 0, 0, thumbWidth, thumbHeight);

    const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.9);
    return thumbnailUrl.length > 10 ? thumbnailUrl : null;
}

// extract video duration and preview image:
export async function extractVideoDetails(file, withThumbnail = false) {
    return new Promise((resolve) => {
        let duration, width, height;
        let timeout;

        const video = document.createElement('video');
        const url = URL.createObjectURL(file);

        const cleanup = () => {
            clearTimeout(timeout);
            URL.revokeObjectURL(url);
        };

        const resolveDetails = (details = {}) => {
            cleanup();
            resolve({duration, width, height, ...details});
        };

        video.src = url;

        video.addEventListener('loadedmetadata', (e) => {
            duration = video.duration;
            width = video.videoWidth;
            height = video.videoHeight;

            // extracting image doesn't work in ios/safari:
            if (!withThumbnail || isSafari) { return resolveDetails(); }

            const onUpdate = () => {
                video.removeEventListener('timeupdate', onUpdate);

                const thumbnailUrl = createThumbnailUrl(video, width, height);
                if (thumbnailUrl) {
                    resolveDetails({thumbnailUrl});
                }
                else {
                    console.log('could not extract frame from video');
                    resolveDetails();
                }
            };

            video.addEventListener('timeupdate', onUpdate);
            video.currentTime = 0;
        });

        video.addEventListener('error', (err) => {
            console.log('video error:', video.error?.message);
            resolveDetails();
        });

        video.load();

        timeout = setTimeout(() => {
            resolveDetails();
        }, 1000);
    });
}

export async function extractImageDetails(file) {
    return new Promise((resolve) => {
        let width, height;
        let timeout;

        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const url = URL.createObjectURL(file);

        const cleanup = () => {
            clearTimeout(timeout);
            URL.revokeObjectURL(url);
        };

        const resolveDetails = (details = {}) => {
            cleanup();
            resolve({width, height, ...details});
        };

        img.onload = () => {
            width = img.naturalWidth || img.width;
            height = img.naturalHeight || img.height;

            const thumbnailUrl = createThumbnailUrl(img, width, height);
            if (thumbnailUrl) {
                resolveDetails({thumbnailUrl});
            }
            else {
                console.log('could not extract thumbnail from image');
                resolveDetails();
            }
        };

        img.onerror = () => {
            console.log('image error');
            resolveDetails();
        };

        img.src = url;

        timeout = setTimeout(() => {
            resolveDetails();
        }, 1000);
    });
}


export const UploadError = createError('UploadError');
export const AbortError = createError('AbortError');

function createXhrUpload(blob, url, onProgress, {contentType} = {}) {
    const xhr = new XMLHttpRequest(); // using xhr for upload progress events

    const done = new Promise((resolve, reject) => {
        xhr.open("PUT", url, true);
        if (contentType) {
            xhr.setRequestHeader("Content-Type", contentType);
        }

        if (onProgress) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    onProgress(event.loaded, event.total);
                }
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve({etag: xhr.getResponseHeader('ETag')});
            } else {
                reject(new UploadError(`Upload failed: ${parseUploadError(xhr)}`));
            }
        }

        xhr.onerror = () => reject(new UploadError("Upload failed (network error)"));
        xhr.onabort = () => reject(new AbortError("Upload aborted"));

        xhr.send(blob);
    });

    return {
        done: () => done,
        abort: () => xhr.abort(),
    };
}

function uploadSingleFile(file, url, onProgress) {
    const upload = createXhrUpload(
        file,
        url,
        (loaded, total) => onProgress?.(total ? loaded / total : 0),
        {contentType: file.type}
    );

    return {
        done: async () => {
            await upload.done();
        },
        abort: upload.abort,
    };
}

function uploadMultipartFile(file, upload, onProgress, handlers = {}) {
    const partSize = Number(upload?.part_size);
    if (!partSize) {
        throw new UploadError('Upload failed: invalid multipart upload configuration');
    }
    if (!handlers.requestPartUrl || !handlers.completeMultipartUpload) {
        throw new UploadError('Upload failed: incomplete multipart upload handlers');
    }

    let aborted = false;
    let currentUpload = null;
    let abortedRemotely = false;
    let uploadedBytes = 0;
    const totalParts = Math.ceil(file.size / partSize);

    const abortRemote = async () => {
        if (abortedRemotely || !handlers.abortMultipartUpload) {
            return;
        }

        abortedRemotely = true;

        try {
            await handlers.abortMultipartUpload();
        }
        catch (err) {
            console.log('[uploadFile] failed to abort multipart upload', err);
        }
    };

    const done = (async () => {
        const parts = [];

        try {
            for (let index = 0; index < totalParts; index += 1) {
                if (aborted) {
                    throw new AbortError('Upload aborted');
                }

                const partNumber = index + 1;
                const start = index * partSize;
                const end = Math.min(start + partSize, file.size);
                const partBlob = file.slice(start, end);
                const {url} = await handlers.requestPartUrl(partNumber);

                if (aborted) {
                    throw new AbortError('Upload aborted');
                }

                currentUpload = createXhrUpload(
                    partBlob,
                    url,
                    (loaded) => onProgress?.((uploadedBytes + loaded) / file.size),
                    {}
                );

                const {etag} = await currentUpload.done();
                currentUpload = null;

                if (!etag) {
                    throw new UploadError(`Upload failed: missing part ETag for part ${partNumber}`);
                }

                parts.push({PartNumber: partNumber, ETag: etag});
                uploadedBytes += partBlob.size;
                onProgress?.(uploadedBytes / file.size);
            }

            await handlers.completeMultipartUpload(parts);
        }
        catch (err) {
            currentUpload = null;

            if (aborted || err instanceof AbortError) {
                await abortRemote();
                throw new AbortError('Upload aborted');
            }

            await abortRemote();
            throw err;
        }
    })();

    return {
        done: () => done,
        abort: () => {
            aborted = true;
            currentUpload?.abort();
        },
    };
}

export function uploadFile(file, upload, onProgress, handlers) {
    if (typeof upload === 'string') {
        return uploadSingleFile(file, upload, onProgress);
    }

    if (upload?.strategy === 'multipart') {
        return uploadMultipartFile(file, upload, onProgress, handlers);
    }

    if (upload?.url) {
        return uploadSingleFile(file, upload.url, onProgress);
    }

    throw new UploadError('Upload failed: invalid upload configuration');
}

function parseUploadError(xhr) {
    const contentType = xhr.getResponseHeader("Content-Type") || "";
    const text = xhr.responseText || "";

    if (contentType.includes("xml") || text.startsWith("<")) {
        try {
            const xml = new DOMParser().parseFromString(text, "application/xml");
            const code = xml.querySelector("Code")?.textContent;
            const message = xml.querySelector("Message")?.textContent;
            if (code || message) {
                return `${xhr.status} ${code || "Error"}: ${message || "Unknown error"}`;
            }
        } catch {}
    }

    return `HTTP ${xhr.status}: ${xhr.responseText || xhr.statusText || "Upload failed"}`;
}

export function downloadUrl(url, filename = '') {
    window.skipBeforeUnload = true;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);

    setTimeout(() => { window.skipBeforeUnload = false; }, 100);
}

export function shareSocial(platform, link) {
    const text = 'Check out this video I optimized!';
    const url = encodeURIComponent(link);
    const shareUrl = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        x: `https://x.com/intent/post?url=${url}&text=${encodeURIComponent(text)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
        threads: `https://threads.net/intent/post?text=${encodeURIComponent(text)}%20${url}`
    }[platform];
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
    mixpanel?.track('share_to_social', {share_id: link.split('/').pop(), share_channel: platform});
}

export function getThumbnailUrl(inputPath) {
    const session_id = inputPath?.split('/')[0];
    return `${config.thumbnailsUrl}/${session_id}/thumbnail.png`;
}

export function getPreviewImageUrl(inputPath) {
    const session_id = inputPath?.split('/')[0];
    return `${config.thumbnailsUrl}/${session_id}/preview.jpg`;
}

export function getSmallPreviewImageUrl(inputPath) {
    const session_id = inputPath?.split('/')[0];
    return `${config.thumbnailsUrl}/${session_id}/preview-small.jpg`;
}

export function gcd(a, b) {
    if (!b) { return a; }
    while (b !== 0) {
        [a, b] = [b, a % b];
    }
    return a;
}

export function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function throttle(fn, wait = 100) {
    let last = 0, timeout, ctx, args;

    function throttled(...a) {
        ctx = this; args = a;
        const now = Date.now(), remaining = wait - (now - last);
        if (remaining <= 0) {
            if (timeout) clearTimeout(timeout), timeout = null;
            last = now;
            fn.apply(ctx, args);
        } else if (!timeout) {
            timeout = setTimeout(() => {
                last = Date.now(); timeout = null;
                fn.apply(ctx, args);
            }, remaining);
        }
    }

    throttled.cancel = () => timeout && (clearTimeout(timeout), timeout = null);
    return throttled;
}

export function debounce(func, wait, immediate) {
	var timeout;
	return function() {
		var context = this, args = arguments;
		var later = function() {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		var callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) func.apply(context, args);
	};
};

export function formatBytesParts(bytes, decimals = 0) {
    if (!bytes || bytes <= 0) { return {size: '0', unit: 'MB'}; }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = (decimals > 0 && i > 1)
        ? (bytes / Math.pow(k, i)).toFixed(decimals).replace(/\.?0+$/,'')
        : Math.floor(bytes / Math.pow(k, i)).toString();

    return {size, unit: sizes[i]};
}

export function formatBytes(bytes, decimals = 0, space = true, unit = true) {
    const formatted = formatBytesParts(bytes, decimals);
    return formatted.size + (space ? ' ' : '') + (unit ? formatted.unit : '');
}

export function formatTime(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
        return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
}

export function formatDuration(minutes) {
    if (minutes < 60) {
        return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else {
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hrs} hr${hrs !== 1 ? 's' : ''}` + (mins > 0 ? ` ${mins} min${mins !== 1 ? 's' : ''}` : '');
    }
}

export function timeAgo(date) {
    if (!date) { return ''; }
    const diff = (Date.now() - new Date(date)) / 1000;
    const units = {year:31536000, month:2592000, day:86400, hour:3600, minute:60};
    const [u,v] = Object.entries(units).find(([_,s]) => diff >= s) || ['second', 1];
    return new Intl.RelativeTimeFormat('en', {numeric: 'auto'}).format(-Math.floor(diff/v), u);
}

export function formatDate(dateStr, formatOpts = {}, locale = 'en-GB') {
    if (!dateStr) { return ''; }
    const d = new Date(dateStr);
    let formatted = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        ...formatOpts
    }).format(d);
    if (d.getFullYear() !== new Date().getFullYear() && !formatOpts.year) {
        formatted = `${formatted} ${d.getFullYear()}`;
    }
    return formatted;
}

export function formatDateFull(dateStr) {
    if (!dateStr) { return ''; }
    const d = new Date(dateStr);
    let formatted = new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(d);
    return formatted;
}

export function setQueryParam(searchParams, key, value) {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
        nextParams.set(key, value);
    }
    else {
        nextParams.delete(key);
    }

    return nextParams;
}

export function getJobName(job, fallback = undefined) {
    return job?.display_name || job?.input_path?.split('/').pop() || fallback;
}

export function calcSavingsPercent(project) {
    if (Number(project.total_input_size) > 0 && Number(project.total_output_size) > 0) {
        return Math.max(0, Math.round(
            100 * (Number(project.total_input_size) - Number(project.total_output_size)) / Number(project.total_input_size)
        ));
    }
    return 0;
}

export function capitalize(str) {
    if (!str) { return str; }
    return str[0].toUpperCase() + str.slice(1);
}

export function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function filterJobs(jobs, filterBy) {
    if (filterBy === 'all') { return jobs; }
    const now = Date.now();
    const days = filterBy === '7d' ? 7 : filterBy === '30d' ? 30 : 0;
    return jobs.filter(j => {
        const createdAt = new Date(j.created_at).getTime();
        return (now - createdAt) <= days * 24 * 60 * 60 * 1000;
    });
}

export function sortJobs(allJobs, sortBy) {
    let jobs = [...allJobs];
    if (sortBy === 'newest') {
        jobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    else if (sortBy === 'oldest') {
        jobs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    else if (sortBy === 'size') {
        jobs.sort((a, b) => (b.output_size || 0) - (a.output_size || 0));
    }
    else if (sortBy === 'name') {
        jobs.sort((a, b) => {
            const nameA = (a.display_name || a.input_path?.split('/').pop() || '').toLowerCase();
            const nameB = (b.display_name || b.input_path?.split('/').pop() || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }
    return jobs;
}

export function sortProjects(allProjects, sortBy) {
    let projs = [...allProjects];
    if (sortBy === 'modified') {
        projs.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }
    else if (sortBy === 'newest') {
        projs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    else if (sortBy === 'oldest') {
        projs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    else if (sortBy === 'size') {
        projs.sort((a, b) => (b.total_output_size || 0) - (a.total_output_size || 0));
    }
    else if (sortBy === 'name') {
        projs.sort((a, b) => a.name.localeCompare(b.name));
    }
    return projs;
}

export function getClosestRatio(w, h) {
    if (!w || !h) { return null; }
    const ratio = w / h;
    if (ratio >= 1.70 && ratio <= 1.85) { return '16:9'; }
    else if (ratio >= 0.54 && ratio <= 0.59) { return '9:16'; }
    else if (ratio >= 0.95 && ratio <= 1.05) { return '1:1'; }
    else if (ratio >= 1.28 && ratio <= 1.38) { return '4:3'; }
    else if (ratio >= 0.75 && ratio <= 0.85) { return '4:5'; }
    return null;
}

export function getAspectRatio(w, h, max = 20) {
    if (!w || !h) { return null; }
    const ratio = w / h;
    let bestNum = 1, bestDen = 1, bestError = Infinity;
    for (let den = 1; den <= max; den++) {
        const num = Math.round(ratio * den);
        const error = Math.abs(ratio - num / den);
        if (error < bestError) {
            bestError = error;
            bestNum = num;
            bestDen = den;
        }
    }
    const g = gcd(bestNum, bestDen);
    return `${bestNum / g}:${bestDen / g}`;
}

export function getAspectRatioDimensions(aspectRatio) {
    if (!aspectRatio) { return null; }

    const [width, height] = String(aspectRatio).split(':').map(Number);
    if (!width || !height) { return null; }

    return { width, height };
}

export function isAspectRatioClose(dimensions, aspectRatio, tolerance = 0) {
    const width = Number(dimensions?.width);
    const height = Number(dimensions?.height);
    const ratioDimensions = getAspectRatioDimensions(aspectRatio);

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 || !ratioDimensions) {
        return false;
    }

    const ratioValue = width / height;
    const targetValue = ratioDimensions.width / ratioDimensions.height;
    return Math.abs(ratioValue - targetValue) <= tolerance;
}

export function getExactAspectRatio(dimensions) {
    const width = Math.round(Number(dimensions?.width));
    const height = Math.round(Number(dimensions?.height));
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return null;
    }

    const divisor = gcd(width, height);
    return `${width / divisor}:${height / divisor}`;
}

export function getResolutionSize(resolution) {
    if (Array.isArray(resolution)) { return getResolutionSize(resolution[0]); }
    if (typeof resolution === 'string') {
        const [width, height] = resolution.split(/[×x]/).map(Number);
        return width && height ? {width, height} : null;
    }
    if (!resolution?.width || !resolution?.height) { return null; }
    return {width: Number(resolution.width), height: Number(resolution.height)};
}

export function getJobAspectRatioStr(job) {
    const {width, height} = getResolutionSize(job?.resolutions?.output) || getResolutionSize(job?.resolutions?.input) || getResolutionSize(job?.resolution) || {};
    return getAspectRatio(width, height);
}

let videoPresets;

export function getRelevantPresets(job, ignoreChildJobs = false) {
    if (!videoPresets) {
        videoPresets = JSON.parse( document.querySelector('#data-video-presets').textContent.trim() );
    }

    if (!job?.resolution) { return {presets: [], reason: 'No resolution available for this video'}; }

    const [jw, jh] = job.resolution.split(/[×x]/).map(Number);
    let jobRatio = getClosestRatio(jw, jh);
    if (!jobRatio) { return {presets: [], reason: 'No preset matches this video\'s aspect ratio'}; }

    const presets = videoPresets.filter(p => {
        if (p.type !== 'scale') { return false; }
        if (p.aspect_ratio !== jobRatio) { return false; }
        if (!ignoreChildJobs && job.child_jobs?.some(cj => cj.preset_id === p.id)) { return false; }
        const [pw, ph] = p.resolution.split(/[×x]/).map(Number);
        if (pw > jw || ph > jh) { return false; }
        if (pw === jw && ph === jh) { return false; }
        return true;
    });

    if (presets.length === 0) {
        const reason = `No ${job.child_jobs?.length ? 'additional' : 'relevant'} preset found for this video\'s resolution and aspect ratio`;
        return {presets: [], reason};
    }

    return {presets};
}

export function trackAnyResultDownloaded(props) {
    if (!mixpanel) { return; }
    mixpanel.track('any_result_downloaded', props);
    trackGa4Event('any_result_downloaded', props);
}

export function trackZipDownloadAnalytics({ file_count, total_mb }) {
    if (!mixpanel) { return; }
    const props = {
        download_type: 'zip',
        file_count,
        total_mb,
    };
    mixpanel.track('image_result_downloaded', props);
    trackGa4Event('image_result_downloaded', props);
    trackAnyResultDownloaded(props);
}

export function sendDownloadAnalytics(job) {
    if (!mixpanel) { return; }
    const input_size_mb = Number(job.input_size ? Math.ceil(job.input_size / (1024 * 1024)) : 0);
    const output_size_mb = Number(job.output_size ? Math.ceil(job.output_size / (1024 * 1024)) : 0);
    const mediaType = getMediaType(job.input_path);
    const format = getFileFormat(job.input_path);

    const eventName = mediaType === 'video' ? 'optimized_video_downloaded' : 'image_result_downloaded';
    const props = {
        operation: mediaType === 'video'
            ? (job.preset_id === 'default' ? 'optimize' : 'reframe')
            : (format === 'heic' ? 'heic_to_jpeg' : 'optimize'),
        format,
        input_size_mb, output_size_mb,
        savings_pct: getJobSavingsPercent(job),
        aspect_ratio: job.resolution ? getAspectRatio(...job.resolution.split(/[×x]/).map(Number)) : null,
        download_type: 'single',
    };
    mixpanel?.track(eventName, props);
    if (eventName === 'image_result_downloaded') {
        trackGa4Event('image_result_downloaded', props);
        trackAnyResultDownloaded(props);
    }
}

export function createTrackJob(baseJob) {
    return {
        id: 'smart-track',
        name: 'Smart track',
        preset_id: 'smart-crop',
        // resolution: baseJob?.resolution,
        status: 'draft',
    };
}

export function getFileFormat(filename) {
    const ext = filename?.toLowerCase().split('.').pop();
    return {
        'jpg': 'jpeg',
        'heif': 'heic',
    }[ext] || ext || 'unknown';
}


export function getBreadcrumbs(project, folderId, {
    includeProject = false,
    includeCurrentFolder = false,
} = {}) {
    const breadcrumbs = [{text: 'All projects', link: '/projects'}];

    if (folderId || includeProject) {
        breadcrumbs.push({
            text: project?.name || 'Untitled Project',
            link: `/projects/${project?.id}`
        });

        const folder = project?.folders?.find(f => f.id === folderId);
        if (folder) {
            const parents = [];
            let parentId = folder.parent_folder_id;
            while (parentId) {
                const parent = project.folders.find(f => f.id === parentId);
                if (parent) {
                    parents.unshift(parent);
                    parentId = parent.parent_folder_id;
                }
                else {
                    parentId = null;
                }
            }

            breadcrumbs.push(...parents.map(f => ({
                text: f.name,
                link: `/projects/${project?.id}?folder=${f.id}`
            })));

            if (includeCurrentFolder) {
                breadcrumbs.push({
                    text: folder.name,
                    link: `/projects/${project?.id}?folder=${folder.id}`
                });
            }
        }
    }

    return breadcrumbs;
}

export function getJobSavingsPercent(job) {
    if (!job) { return 0; }
    const inputSize = Number(job.input_size) || 0;
    const outputSize = Number(job.output_size) || 0;
    if (inputSize > 0 && outputSize > 0 && outputSize < inputSize) {
        return Math.floor(100 * (1.0 - (outputSize / inputSize)));
    }
    return 0;
}

export function getJobErrorDetails(job, mediaSource) {
    if (job?.status !== 'failed') { return null; }

    const itemFormatSource = mediaSource || job.input_path || job.file?.name || job.display_name || job.name || '';
    const mediaType = getMediaType(itemFormatSource);

    if (job.validation_error) {
        return {
            header: 'Validation failed',
            msg: `${job.validation_error} (${mediaType} has not been uploaded)`,
        };
    }

    const error = jobErrors[mediaType]?.[job.error_code]
        || jobErrors[job.error_code]
        || jobErrors[999];

    return {
        header: error?.header || 'Optimization failed',
        msg: error?.msg || 'An unknown error occurred',
    };
}

export function getJobErrorMessage(job, mediaSource) {
    return getJobErrorDetails(job, mediaSource)?.msg || null;
}

export function getUserAvatarLetters(user) {
    if (!user) { return 'A'; }
    return user.name
        ? user.name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
        : (user.email?.[0] ?? 'A').toUpperCase();
}

export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function mergeJobThumbs(prevJobs, nextJobs, uploads) {
    const prevJobsById = new Map(prevJobs.map(job => [String(job.id), job]));
    const uploadsByJobId = new Map(
        uploads
            .filter(upload => upload.job_id && upload.thumbnail_url)
            .map(upload => [String(upload.job_id), upload])
    );

    return nextJobs.map(job => {
        const prevJob = prevJobsById.get(String(job.id));
        const upload = uploadsByJobId.get(String(job.id));
        let nextJob = job;

        if (!nextJob.thumbnail_url && !nextJob.has_thumbnail) {
            const localThumbnailUrl = upload?.thumbnail_url || (!prevJob?.has_thumbnail ? prevJob?.thumbnail_url : null);
            if (localThumbnailUrl) {
                nextJob = {...nextJob, thumbnail_url: localThumbnailUrl};
            }
        }

        return prevJob && shallowEqual(prevJob, nextJob) ? prevJob : nextJob;
    });
}

export function shallowEqual(a, b) {
    if (a === b) { return true; }
    if (!a || !b) { return false; }

    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
        if (a[key] !== b[key]) { return false; }
    }
    return true;
}

export function isTerminalJobStatus(status) {
    return ['done', 'failed'].includes(status);
}

export const jobErrors = {
    video: {
        20: {header: "Cannot optimize further", msg: "This video is already highly compressed. Further optimization would significantly reduce quality."},
        111: {header: "Invalid video file", msg: "The video file you've uploaded is not supported or is invalid."},
        101: {header: "Unsupported format or codec", msg: "Sorry, we can't compress this video because the format or codec isn't supported. Please convert to H.264 or H.265 and try again."},
        102: {header: "Damaged file", msg: "Sorry, we can't compress this video because the file appears damaged. Try re-exporting from your source and uploading again."},
        103: {header: "Audio processing error", msg: "Sorry, we can't compress this video due to an audio processing error. Try removing the audio track or re-exporting with different audio settings."},
        104: {header: "Unsupported resolution", msg: "Sorry, we can't compress this video because the resolution is outside our supported range (480p-4K)."},
        105: {header: "File too large", msg: "Sorry, we can't compress this video because it's too large. Please split it into smaller segments or reduce the bitrate."},
        106: {header: "Unsupported frame rate", msg: "Sorry, we can't compress this video because the frame rate isn't supported. Please convert to 24, 25, 30, or 60 fps."},
        107: {header: "Unsupported color sampling", msg: "Sorry, we can't compress this video because it uses 4:2:2 color sampling. Please re-export using H.264 or H.265 format."},
        108: {header: "Unsupported container format", msg: "Sorry, we can't compress this video because the container format isn't supported. Try re-wrapping in MP4 or MOV format."},
        109: {header: "Video too long", msg: "Sorry, we can't compress this video because it's too long. Please split it into segments under 2 hours each."},
        110: {header: "Unsupported bitrate", msg: "Sorry, we can't compress this video because the bitrate is outside our supported range (1-100 Mbps)."},
        200: {header: "File corrupted", msg: "Sorry, we can't compress this video. This might be due to file corruption. Try re-exporting from your editing software and uploading again."},
        201: {header: "Processing took too long", msg: "Sorry, we can't compress this video because processing took too long. Try splitting into shorter segments or reducing resolution."},
        202: {header: "Insufficient processing resources", msg: "Sorry, we can't compress this video due to insufficient processing resources. Please try again in a few minutes or reduce the video resolution."},
        203: {header: "Invalid data", msg: "Sorry, we can't compress this video because it contains invalid data. Try re-exporting with standard settings from your editing software."},
        301: {header: "Upload incomplete", msg: "Sorry, we can't compress this video because the upload didn't complete. Check your internet connection and try uploading again."},
        302: {header: "File access error", msg: "Sorry, we can't compress this video because we couldn't access the file. This is likely temporary - please try again in a few moments."},
        303: {header: "Storage system issue", msg: "Sorry, we can't compress this video due to a storage system issue. Please try again shortly."},
        401: {header: "Session expired", msg: "Sorry, we can't compress this video because your session expired. Please log in again and resubmit."},
        402: {header: "Processing limit reached", msg: "Sorry, we can't compress this video because you've reached your processing limit. Upgrade your plan or wait until your quota resets."},
        403: {header: "Account access restricted", msg: "Sorry, we can't compress this video because your account access has been restricted. Please contact support."},
        501: {header: "Server error", msg: "Sorry, we can't compress this video due to a server error on our end. Please try again in a few minutes."},
        502: {header: "Service offline", msg: "Sorry, we can't compress this video because our service is temporarily offline. We're working to restore it - please try again soon."},
        503: {header: "High demand", msg: "Sorry, we can't compress this video right now due to high demand. Please wait a few minutes and try again."},
    },
    image: {
        24: {header: "Invalid JPEG file", msg: "Sorry, we can't compress this image because the JPEG file is invalid or corrupted."},
        39: {header: "Cannot optimize further", msg: "This image is already highly optimized. Further optimization would significantly reduce quality."},
        44: {header: "Cannot optimize further", msg: "This image is already highly optimized. Further optimization would significantly reduce quality."},
        118: {header: "Invalid HEIC file", msg: "Sorry, we can't compress this image because the file is invalid or corrupted."},
    },
    999: {header: "Optimization failed", msg: "Sorry, we can't compress this video due to an unexpected error. Please try again or contact support if this persists."},
    9999: {header: "Timed out", msg: "Sorry, we can't compress this item because the operation timed out. Please try again or contact support if this persists."},
};